import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  ChartOptions,
} from 'chart.js';
import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import sumBy from 'lodash/sumBy';

ChartJS.register(
  TimeScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface Props {
  chartData: Record<string, DetergentReading[]>;
  detergents: Detergents[];
  washCounts: { date: moment.Moment; count: number }[];
  hourlyWashCounts: {
    count: number;
    countByProduct: { [key: string]: number };
    countsBySiteId: { [key: number]: number };
  }[];
  dayRange?: number;
}

interface DetergentReading {
  id: number;
  timestamp: string;
  value: string;
  detergentContainerId: number;
  detergentId: number;
}

interface Detergents {
  id: number;
  label: string;
  currentValue: number | null;
  currentCostPerCar: number;
  goalCostPerCar: number;
  detergentColor: string;
}

interface ChartPoint {
  x: string;
  y: number;
}

interface ChartDataset {
  label: string;
  data: ChartPoint[];
  borderColor: string;
  backgroundColor: string;
  borderDash?: number[];
  tension?: number;
  pointRadius?: number;
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  yAxisID?: string;
}

const getTimeScaleConfig = (range: number) => {
  if (range <= 7) return { unit: 'day' as const, maxTicksLimit: 7 };
  if (range <= 30) return { unit: 'day' as const, maxTicksLimit: 7 };
  return { unit: 'week' as const, maxTicksLimit: 7 };
};

const DetergentTracker = ({
  chartData,
  detergents,
  washCounts,
  hourlyWashCounts,
  dayRange = 7,
}: Props) => {
  const [chartInfo, setChartInfo] = useState<{
    data: {
      datasets: ChartDataset[];
      total: string;
    };
    options: ChartOptions<'line'>;
  }>({
    data: { datasets: [], total: '0' },
    options: {},
  });

  useEffect(() => {
    const currentDay = moment().startOf('day');
    const days = _.range(dayRange).map((i) =>
      currentDay.clone().subtract(dayRange - 1 - i, 'days')
    );
    const timeScale = getTimeScaleConfig(dayRange);

    const datasets: ChartDataset[] = [];

    // chartData is keyed by detergentId (the API groups readings by detergentId).
    Object.entries(chartData).forEach(([detergentIdKey, readings]) => {
      if (!readings.length) return;

      const grouped = _.groupBy(readings, (r) => moment(r.timestamp).startOf('day').format());
      const data: ChartPoint[] = [];
      let lastValue = 0;
      const detergentId = readings[0]?.detergentId;
      const detergent = detergents.find((d) => d.id === detergentId);
      const label = detergent?.label || `Detergent ${detergentIdKey}`;
      const lineColor = detergent?.detergentColor;

      for (const day of days) {
        const dayKey = day.format();
        const entries = grouped[dayKey];

        if (entries && entries.length > 0) {
          const lastEntry = _.maxBy(entries, (r) => r.timestamp);
          lastValue = lastEntry ? parseFloat(lastEntry.value) : lastValue;
        }

        data.push({ x: day.toISOString(), y: lastValue });
      }

      datasets.push({
        label,
        data,
        borderColor: lineColor || '#000000',
        backgroundColor: 'rgba(0,0,0,0)',
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: lineColor || '#000000',
        pointBorderColor: lineColor,
      });
    });

    // Prepare merged daily wash counts (including today's total from hourly)
    const mergedWashCounts: ChartPoint[] = [];

    const today = moment().startOf('day');
    const totalCounts: number[] = [];

    for (let hour = 0; hour < 24; hour++) {
      const value = hourlyWashCounts[hour] || { count: 0 };
      totalCounts.push(value.count);
    }

    const totalTodayCount = sumBy(totalCounts);

    for (const w of washCounts) {
      const date = moment(w.date).startOf('day');
      if (date.isSame(today, 'day')) continue;
      mergedWashCounts.push({
        x: date.toISOString(),
        y: w.count,
      });
    }

    mergedWashCounts.push({
      x: today.toISOString(),
      y: totalTodayCount,
    });

    datasets.push({
      label: 'Wash Counts',
      data: mergedWashCounts,
      borderColor: '#000000',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      borderDash: [4, 4],
      tension: 0.4,
      pointRadius: 3,
      pointBackgroundColor: '#000000',
      pointBorderColor: '#000000',
      yAxisID: 'y1',
    });

    setChartInfo({
      data: {
        datasets,
        total: '0',
      },
      options: {
        animation: {
          duration: 0,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: timeScale.unit,
              tooltipFormat: 'MMM D',
            },
            ticks: {
              maxTicksLimit: timeScale.maxTicksLimit,
            },
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              display: false,
            },
            position: 'left',
            title: {
              display: true,
              text: 'Detergent (gal)',
              color: '#000000',
              font: {
                weight: 'bold',
              },
            },
            ticks: {
              color: '#000000',
            },
          },
          y1: {
            beginAtZero: true,
            grid: {
              display: false,
            },
            position: 'right',
            title: {
              display: true,
              text: 'Wash Counts',
              color: '#000000',
              font: {
                weight: 'bold',
              },
            },
            ticks: {
              color: '#000000',
            },
          },
        },
        plugins: {
          legend: {
            display: true,
          },
          datalabels: {
            display: false,
          },
        },
      },
    });
  }, [chartData, detergents, washCounts, hourlyWashCounts, dayRange]);

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <Line data={chartInfo.data} options={chartInfo.options} />
    </div>
  );
};

export default DetergentTracker;
